import { Backdrop, Box, Button, Typography } from '@mui/material';
import { keyframes } from '@emotion/react';
import type { Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import type { PalMode } from '../PalAssessmentWrapper';

interface IProps {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  currentPalModeKey: PalMode;
}

const popupContent: Record<
  PalMode,
  { emoji: string; title: string; subtitle: string; chip: string }
> = {
  warmup: {
    emoji: '☀️',
    title: 'PAL_MCP_WARMUP_TITLE',
    subtitle: 'PAL_MCP_WARMUP_SUBTITLE',
    chip: 'PAL_MCP_WARMUP_CHIP',
  },
  practice: {
    emoji: '✏️',
    title: 'PAL_MCP_PRACTICE_TITLE',
    subtitle: 'PAL_MCP_PRACTICE_SUBTITLE',
    chip: 'PAL_MCP_PRACTICE_CHIP',
  },
  learning: {
    emoji: '📚',
    title: 'PAL_MCP_LEARNING_TITLE',
    subtitle: 'PAL_MCP_LEARNING_SUBTITLE',
    chip: 'PAL_MCP_LEARNING_CHIP',
  },
  challenge: {
    emoji: '⚡',
    title: 'PAL_MCP_CHALLENGE_TITLE',
    subtitle: 'PAL_MCP_CHALLENGE_SUBTITLE',
    chip: 'PAL_MCP_CHALLENGE_CHIP',
  },
};

const chipStyleByMode: Record<
  PalMode,
  { background: string; color: string; boxShadow: string }
> = {
  warmup: {
    background: 'linear-gradient(135deg, #fef3c7, #fde68a)',
    color: '#92400e',
    boxShadow: '0 4px 16px rgba(245, 158, 11, 0.3)',
  },
  learning: {
    background: 'linear-gradient(135deg, #dbeafe, #bfdbfe)',
    color: '#1e40af',
    boxShadow: '0 4px 16px rgba(59, 130, 246, 0.3)',
  },
  practice: {
    background: 'linear-gradient(135deg, #d1fae5, #a7f3d0)',
    color: '#065f46',
    boxShadow: '0 4px 16px rgba(16, 185, 129, 0.3)',
  },
  challenge: {
    background: 'linear-gradient(135deg, #ede9fe, #ddd6fe)',
    color: '#5b21b6',
    boxShadow: '0 4px 16px rgba(139, 92, 246, 0.3)',
  },
};

const modeChipPulseGlow = keyframes`
  0%, 100% {
    transform: translateY(0) scale(1);
    filter: brightness(1);
    box-shadow: var(--chip-shadow);
  }
  50% {
    transform: translateY(-3px) scale(1.02);
    filter: brightness(1.08);
    box-shadow: var(--chip-shadow-boost);
  }
`;

export const PalModeChangePopup = ({
  open,
  setOpen,
  currentPalModeKey,
}: IProps) => {
  const { t } = useTranslation();
  const content = popupContent[currentPalModeKey];
  const chipStyle = chipStyleByMode[currentPalModeKey];

  return (
    <Backdrop
      open={open}
      sx={{
        zIndex: 1400,
        bgcolor: 'rgba(17, 24, 39, 0.55)',
        backdropFilter: 'blur(3px)',
      }}
    >
      <Box
        sx={{
          width: { xs: '90%', sm: 420 },
          maxWidth: 420,
          bgcolor: 'white',
          borderRadius: '16px',
          px: 4,
          py: 4,
          textAlign: 'center',
        }}
      >
        <Typography sx={{ fontSize: 34, mb: '16px' }}>
          {content.emoji}
        </Typography>
        <Typography
          sx={{ fontSize: 36, lineHeight: 1.1, fontWeight: 800, mb: '8px' }}
        >
          {t(content.title)}
        </Typography>
        <Typography
          sx={{ fontSize: 14, color: '#6b7280', lineHeight: 1.5, mb: '16px' }}
        >
          {t(content.subtitle)}
        </Typography>
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '10px',
            px: '24px',
            py: '12px',
            borderRadius: '50px',
            background: chipStyle.background,
            color: chipStyle.color,
            boxShadow: chipStyle.boxShadow,
            '--chip-shadow': chipStyle.boxShadow,
            '--chip-shadow-boost': chipStyle.boxShadow.replace('0.3)', '0.55)'),
            fontWeight: 700,
            fontSize: 16,
            mb: '28px',
            willChange: 'transform, filter, box-shadow',
            animation: `${modeChipPulseGlow} 1.6s ease-in-out infinite`,
          }}
        >
          {t(content.chip)}
        </Box>
        <Box>
          <Button
            onClick={() => setOpen(false)}
            sx={{
              minWidth: 160,
              px: '16px',
              py: '8px',
              textTransform: 'none',
              borderRadius: '10px',
              color: 'white',
              fontWeight: 700,
              fontSize: 16,
              background: 'linear-gradient(180deg, #384766 0%, #1f2d47 100%)',
              '&:hover': {
                background: 'linear-gradient(180deg, #2f3f5d 0%, #18243a 100%)',
              },
            }}
          >
            {currentPalModeKey === 'challenge'
              ? t('PAL_MCP_BTN_ACCEPT_CHALLENGE')
              : currentPalModeKey == 'practice'
              ? t('PAL_MCP_BTN_LETS_PRACTICE')
              : currentPalModeKey == 'learning'
              ? t('PAL_MCP_BTN_START_LEARNING')
              : t('PAL_MCP_BTN_LETS_GO')}
          </Button>
        </Box>
      </Box>
    </Backdrop>
  );
};

export default PalModeChangePopup;
